import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { View, StyleSheet, PanResponder } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { HierarchyPosition } from './BaseHierarchy';

// Generic interfaces for any hierarchy item
export interface HierarchyItemData {
  _id: string;
  [key: string]: any;
}

export interface DraggableHierarchyItemHandle {
  setPosition: (position: HierarchyPosition) => void;
  getPosition: () => HierarchyPosition;
  animateToPosition: (position: HierarchyPosition) => void;
}

export interface DraggableHierarchyItemProps {
  item: HierarchyItemData;
  initialPosition: HierarchyPosition;
  onPositionChange: (itemId: string, position: HierarchyPosition) => void;
  onPositionUpdate?: (itemId: string, position: HierarchyPosition) => void;
  onSnap?: (target: string, source: string) => void;
  dragEnabled?: boolean;
  zoomLevel?: number;
  children: (props: {
    position: HierarchyPosition;
    isDragging: boolean;
    gestureHandler: any;
    animatedStyle: any;
    setPosition: (position: HierarchyPosition) => void;
  }) => React.ReactNode;
  // Optional styling props
  itemWidth?: number;
  itemHeight?: number;
  borderRadius?: number;
  shadowEnabled?: boolean;
}

const DraggableHierarchyItem = forwardRef<DraggableHierarchyItemHandle, DraggableHierarchyItemProps>(({
  item,
  initialPosition,
  onPositionChange,
  onPositionUpdate,
  onSnap,
  dragEnabled = true,
  zoomLevel = 1,
  children,
  itemWidth = 200,
  itemHeight = 120,
  borderRadius = 12,
  shadowEnabled = true,
}, ref) => {
  const [isDragging, setIsDragging] = useState(false);
  const hasBeenInitialized = useRef(false);

  // Animated values for position
  const translationX = useSharedValue(initialPosition.x);
  const translationY = useSharedValue(initialPosition.y);

  // Initialize position on mount
  React.useEffect(() => {
    if (!hasBeenInitialized.current) {
      translationX.value = initialPosition.x;
      translationY.value = initialPosition.y;
      hasBeenInitialized.current = true;
    }
  }, [initialPosition.x, initialPosition.y, translationX, translationY]);

  // Imperative methods
  const setPosition = useCallback((position: HierarchyPosition) => {
    translationX.value = position.x;
    translationY.value = position.y;
  }, [translationX, translationY]);

  const getPosition = useCallback(() => {
    return {
      x: translationX.value,
      y: translationY.value,
    };
  }, [translationX, translationY]);

  const animateToPosition = useCallback((position: HierarchyPosition) => {
    translationX.value = withSpring(position.x, {
      damping: 20,
      stiffness: 300,
    });
    translationY.value = withSpring(position.y, {
      damping: 20,
      stiffness: 300,
    });
  }, [translationX, translationY]);

  useImperativeHandle(ref, () => ({
    setPosition,
    getPosition,
    animateToPosition,
  }), [setPosition, getPosition, animateToPosition]);

  // Handle position updates (continuous during drag)
  const handlePositionUpdate = useCallback((position: HierarchyPosition) => {
    if (onPositionUpdate) {
      onPositionUpdate(item._id, position);
    }
  }, [item._id, onPositionUpdate]);

  // Handle final position change (on drop)
  const handlePositionChange = useCallback((position: HierarchyPosition) => {
    onPositionChange(item._id, position);
  }, [item._id, onPositionChange]);

  // PanResponder for dragging (matching AcademyHierarchy pattern)
  const dragPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => dragEnabled && !isDragging,
    onPanResponderGrant: (event, gestureState) => {
      if (dragEnabled) {
        setIsDragging(true);
      }
    },
    onPanResponderMove: (event, gestureState) => {
      if (!isDragging || !dragEnabled) return;

      const { dx, dy } = gestureState;
      const newX = initialPosition.x + dx / zoomLevel;
      const newY = initialPosition.y + dy / zoomLevel;

      translationX.value = newX;
      translationY.value = newY;

      // Update position for real-time tracking (continuous updates)
      handlePositionUpdate({
        x: newX,
        y: newY,
      });
    },
    onPanResponderRelease: (event, gestureState) => {
      if (!isDragging || !dragEnabled) return;

      setIsDragging(false);

      // Snap to grid for clean positioning
      const gridSize = 50;
      const snappedX = Math.round(translationX.value / gridSize) * gridSize;
      const snappedY = Math.round(translationY.value / gridSize) * gridSize;

      translationX.value = withSpring(snappedX);
      translationY.value = withSpring(snappedY);

      // Final position update (on drop)
      const finalPosition = {
        x: snappedX,
        y: snappedY,
      };

      handlePositionChange(finalPosition);
    },
  });

  // Animated style for the draggable item
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: translationX.value },
        { translateY: translationY.value },
      ],
      zIndex: isDragging ? 1000 : 1,
    };
  });

  return (
    <Animated.View style={[animatedStyle]}>
      <View
        style={[
          styles.container,
          {
            width: itemWidth,
            height: itemHeight,
            borderRadius,
            ...(shadowEnabled && styles.shadow),
          }
        ]}
        {...dragPanResponder.panHandlers}
      >
        {children({
          position: { x: translationX.value, y: translationY.value },
          isDragging,
          gestureHandler: dragPanResponder.panHandlers,
          animatedStyle,
          setPosition,
        })}
      </View>
    </Animated.View>
  );
});

DraggableHierarchyItem.displayName = 'DraggableHierarchyItem';

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
  },
  shadow: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
  },
});

export default DraggableHierarchyItem;
